<template>
  <div class="side-menu-player" ref="sideMenuPlayerRef">
    <aside class="side-menu-player__filters" :aria-label="texts.filtersAria">
      <div class="side-menu-player__tag">{{ texts.tag }}</div>

      <div class="side-menu-player__filters-desktop">
        <div class="side-menu-player__section side-menu-player__language-section">
          <h3 class="side-menu-player__section-title">{{ texts.languageTitle }}</h3>
          <div class="side-menu-player__language-list x-scrollbar">
            <button
              v-for="lang in languageOptions"
              :key="lang.value"
              type="button"
              class="side-menu-player__language-item"
              :class="{
                'side-menu-player__language-item--active': selectedLanguage === lang.value,
              }"
              @click="selectLanguage(lang.value)"
            >
              <span class="side-menu-player__language-label">{{ lang.label }}</span>
              <IconCheck
                v-if="selectedLanguage === lang.value"
                class="side-menu-player__check-icon"
              />
            </button>
          </div>
        </div>

        <div class="side-menu-player__section">
          <h3 class="side-menu-player__section-title">{{ texts.genderTitle }}</h3>
          <div
            ref="genderDropdownRef"
            class="side-menu-player__dropdown"
            @click.stop="toggleGenderDropdown"
          >
            <span class="side-menu-player__dropdown-label">{{ currentGenderLabel }}</span>
            <IconArrow
              class="side-menu-player__dropdown-icon"
              :class="{ 'side-menu-player__dropdown-icon--open': showGenderDropdown }"
            />
          </div>
        </div>

        <div class="side-menu-player__section">
          <h3 class="side-menu-player__section-title">{{ texts.styleTitle }}</h3>
          <div
            ref="styleDropdownRef"
            class="side-menu-player__dropdown"
            @click.stop="toggleStyleDropdown"
          >
            <span class="side-menu-player__dropdown-label">{{ currentStyleLabel }}</span>
            <IconArrow
              class="side-menu-player__dropdown-icon"
              :class="{ 'side-menu-player__dropdown-icon--open': showStyleDropdown }"
            />
          </div>
        </div>
      </div>

      <div class="side-menu-player__filters-mobile">
        <div class="side-menu-player__section">
          <h3 class="side-menu-player__section-title">{{ texts.languageTitle }}</h3>
          <Dropdown
            ref="languageMobileDropdownRef"
            trigger="click"
            position="bottomLeft"
            strategy="absolute"
            :gap="[0, 4]"
            :content-match-trigger-width="true"
          >
            <template #trigger="{ active }">
              <div
                class="side-menu-player__mobile-dropdown"
                @click="closeOtherMobileDropdowns('language')"
              >
                <span class="side-menu-player__mobile-dropdown-label">
                  {{ currentLanguageLabel }}
                </span>
                <IconArrow
                  class="side-menu-player__mobile-dropdown-icon"
                  :class="{ 'side-menu-player__mobile-dropdown-icon--open': active }"
                />
              </div>
            </template>
            <template #content>
              <div class="side-menu-player__mobile-dropdown-menu x-scrollbar">
                <button
                  v-for="lang in languageOptions"
                  :key="lang.value"
                  type="button"
                  class="side-menu-player__mobile-dropdown-item"
                  :class="{
                    'side-menu-player__mobile-dropdown-item--active':
                      selectedLanguage === lang.value,
                  }"
                  @click="selectLanguageFromMobileDropdown(lang.value)"
                >
                  {{ lang.label }}
                </button>
              </div>
            </template>
          </Dropdown>
        </div>

        <div class="side-menu-player__section">
          <h3 class="side-menu-player__section-title">{{ texts.genderTitle }}</h3>
          <Dropdown
            ref="genderMobileDropdownRef"
            trigger="click"
            position="bottomLeft"
            strategy="absolute"
            :gap="[0, 4]"
            :content-match-trigger-width="true"
          >
            <template #trigger="{ active }">
              <div
                class="side-menu-player__mobile-dropdown"
                @click="closeOtherMobileDropdowns('gender')"
              >
                <span class="side-menu-player__mobile-dropdown-label">
                  {{ currentGenderLabel }}
                </span>
                <IconArrow
                  class="side-menu-player__mobile-dropdown-icon"
                  :class="{ 'side-menu-player__mobile-dropdown-icon--open': active }"
                />
              </div>
            </template>
            <template #content>
              <div class="side-menu-player__mobile-dropdown-menu x-scrollbar">
                <button
                  v-for="gender in genderOptions"
                  :key="gender.value"
                  type="button"
                  class="side-menu-player__mobile-dropdown-item"
                  :class="{
                    'side-menu-player__mobile-dropdown-item--active':
                      selectedGender === gender.value,
                  }"
                  @click="selectGenderFromMobileDropdown(gender.value)"
                >
                  {{ gender.label }}
                </button>
              </div>
            </template>
          </Dropdown>
        </div>

        <div class="side-menu-player__section">
          <h3 class="side-menu-player__section-title">{{ texts.styleTitle }}</h3>
          <Dropdown
            ref="styleMobileDropdownRef"
            trigger="click"
            position="bottomLeft"
            strategy="absolute"
            :gap="[0, 4]"
            :content-match-trigger-width="true"
          >
            <template #trigger="{ active }">
              <div
                class="side-menu-player__mobile-dropdown"
                @click="closeOtherMobileDropdowns('style')"
              >
                <span class="side-menu-player__mobile-dropdown-label">
                  {{ currentStyleLabel }}
                </span>
                <IconArrow
                  class="side-menu-player__mobile-dropdown-icon"
                  :class="{ 'side-menu-player__mobile-dropdown-icon--open': active }"
                />
              </div>
            </template>
            <template #content>
              <div class="side-menu-player__mobile-dropdown-menu x-scrollbar">
                <button
                  v-for="style in styleOptions"
                  :key="style.value"
                  type="button"
                  class="side-menu-player__mobile-dropdown-item"
                  :class="{
                    'side-menu-player__mobile-dropdown-item--active': selectedStyle === style.value,
                  }"
                  @click="selectStyleFromMobileDropdown(style.value)"
                >
                  {{ style.label }}
                </button>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>
    </aside>

    <main class="side-menu-player__content" :aria-label="texts.contentAria">
      <article
        class="side-menu-player__video-wrapper"
        itemscope
        itemtype="https://schema.org/VideoObject"
      >
        <meta itemprop="name" :content="videoTitle" />
        <meta itemprop="description" :content="videoDescription" />
        <meta itemprop="thumbnailUrl" :content="videoPoster" />
        <meta itemprop="contentUrl" :content="videoUrl" />
        <meta itemprop="uploadDate" :content="uploadDate" />
        <meta itemprop="duration" :content="videoDuration" />

        <figure class="side-menu-player__figure">
          <video
            ref="videoRef"
            class="side-menu-player__video"
            :src="videoUrl"
            :poster="videoPoster"
            :title="videoTitle"
            playsinline
            webkit-playsinline
            preload="auto"
            crossorigin="anonymous"
            fetchpriority="high"
            @play="handlePlay"
            @pause="handlePause"
            @ended="handleEnded"
            @timeupdate="handleTimeUpdate"
            @loadedmetadata="handleLoadedMetadata"
            @canplay="handleCanPlay"
            @error="handleError"
          >
            {{ texts.unsupportedText }}
          </video>
          <figcaption class="sr-only">{{ videoTitle }}</figcaption>

          <div class="side-menu-player__controls">
            <PlayBtn
              type="button"
              class="side-menu-player__play-btn"
              :aria-label="isPlaying ? texts.pauseAria : texts.playAria"
              :is-playing="isPlaying"
              @click="togglePlay"
            />
            <div class="side-menu-player__progress-wrapper">
              <div class="side-menu-player__progress" @click="handleProgressClick">
                <div
                  class="side-menu-player__progress-bar"
                  :style="{ width: progressPercent + '%' }"
                />
              </div>
            </div>
          </div>
        </figure>
      </article>
    </main>

    <Teleport to="body">
      <Transition name="dropdown">
        <div v-if="showGenderDropdown" ref="genderMenuRef" class="side-menu-player__dropdown-menu">
          <button
            v-for="gender in genderOptions"
            :key="gender.value"
            type="button"
            class="side-menu-player__dropdown-item"
            :class="{ 'side-menu-player__dropdown-item--active': selectedGender === gender.value }"
            @click.stop="selectGender(gender.value)"
          >
            {{ gender.label }}
          </button>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="dropdown">
        <div v-if="showStyleDropdown" ref="styleMenuRef" class="side-menu-player__dropdown-menu">
          <button
            v-for="style in styleOptions"
            :key="style.value"
            type="button"
            class="side-menu-player__dropdown-item"
            :class="{ 'side-menu-player__dropdown-item--active': selectedStyle === style.value }"
            @click.stop="selectStyle(style.value)"
          >
            {{ style.label }}
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import Dropdown from '@rendora/design/components/dropdown/Dropdown.vue';
import PlayBtn from '@/design/components/buttons/play-btn.vue';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import IconCheck from '~/design/icons/check.svg?component';
import IconArrow from '~/design/icons/dropdown.svg?component';
import type { LanguageOption, Selection, SelectionLabels, SideMenuPlayerTexts } from './types';

const props = withDefaults(
  defineProps<{
    poster?: string;
    defaultSelection?: Selection;
    enablePreload?: boolean;
    languageOptions: LanguageOption[];
    texts: SideMenuPlayerTexts;
    buildDefaultPosterUrl?: (selection: Selection) => string;
    buildVideoUrl: (selection: Selection) => string;
    buildVideoPosterUrl: (selection: Selection) => string;
    buildVideoTitle: (labels: SelectionLabels) => string;
    buildVideoDescription: (labels: SelectionLabels) => string;
  }>(),
  {
    poster: '',
    enablePreload: true,
  },
);

const emit = defineEmits<{
  (e: 'filter-change', filters: Selection): void;
  (e: 'video-play'): void;
  (e: 'video-pause'): void;
  (e: 'video-ended'): void;
  (e: 'video-error', error: Event): void;
}>();

const getFallbackSelection = (): Selection => {
  const firstLanguage = props.languageOptions[0];
  const firstGender = firstLanguage?.genders[0];
  const firstStyle = firstGender?.styles[0];
  return {
    language: firstLanguage?.value || '',
    gender: firstGender?.value || '',
    style: firstStyle?.value || '',
  };
};

const initialSelection = props.defaultSelection || getFallbackSelection();

const selectedLanguage = ref<string>(initialSelection.language);
const selectedGender = ref<string>(initialSelection.gender);
const selectedStyle = ref<string>(initialSelection.style);

const showGenderDropdown = ref(false);
const showStyleDropdown = ref(false);

const genderDropdownRef = ref<HTMLElement | null>(null);
const styleDropdownRef = ref<HTMLElement | null>(null);
const genderMenuRef = ref<HTMLElement | null>(null);
const styleMenuRef = ref<HTMLElement | null>(null);
const languageMobileDropdownRef = ref<InstanceType<typeof Dropdown> | null>(null);
const genderMobileDropdownRef = ref<InstanceType<typeof Dropdown> | null>(null);
const styleMobileDropdownRef = ref<InstanceType<typeof Dropdown> | null>(null);

const sideMenuPlayerRef = ref<HTMLElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
let observer: IntersectionObserver | null = null;

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const isVideoReady = ref(false);

const preloadSelection = computed(() => props.defaultSelection || getFallbackSelection());

const defaultPosterUrl = computed(() => {
  if (props.poster) return props.poster;
  return (
    props.buildDefaultPosterUrl?.(preloadSelection.value) ||
    props.buildVideoPosterUrl(preloadSelection.value)
  );
});

if (props.enablePreload) {
  useHead({
    link: [
      {
        rel: 'preload',
        as: 'image',
        href: defaultPosterUrl.value,
        fetchpriority: 'high',
      },
    ],
  });
}

const genderOptions = computed(() => {
  const selectedLang = props.languageOptions.find(lang => lang.value === selectedLanguage.value);
  return selectedLang?.genders || [];
});

const styleOptions = computed(() => {
  const selectedLang = props.languageOptions.find(lang => lang.value === selectedLanguage.value);
  const selectedGenderOption = selectedLang?.genders.find(g => g.value === selectedGender.value);
  return selectedGenderOption?.styles || [];
});

const hasValidSelection = computed(() => {
  return Boolean(selectedLanguage.value && selectedGender.value && selectedStyle.value);
});

const currentSelection = computed<Selection>(() => ({
  language: selectedLanguage.value,
  gender: selectedGender.value,
  style: selectedStyle.value,
}));

const videoUrl = computed(() => {
  return hasValidSelection.value ? props.buildVideoUrl(currentSelection.value) : '';
});

const videoPoster = computed(() => {
  if (props.poster) return props.poster;
  return hasValidSelection.value ? props.buildVideoPosterUrl(currentSelection.value) : '';
});

const currentLanguageLabel = computed(() => {
  return props.languageOptions.find(opt => opt.value === selectedLanguage.value)?.label || '';
});

const currentGenderLabel = computed(() => {
  return genderOptions.value.find(opt => opt.value === selectedGender.value)?.label || '';
});

const currentStyleLabel = computed(() => {
  return styleOptions.value.find(opt => opt.value === selectedStyle.value)?.label || '';
});

const currentSelectionLabels = computed<SelectionLabels>(() => ({
  language: currentLanguageLabel.value,
  gender: currentGenderLabel.value,
  style: currentStyleLabel.value,
}));

const progressPercent = computed(() => {
  return duration.value === 0 ? 0 : (currentTime.value / duration.value) * 100;
});

const videoTitle = computed(() => {
  return hasValidSelection.value
    ? props.buildVideoTitle(currentSelectionLabels.value)
    : props.texts.emptyTitle;
});

const videoDescription = computed(() => {
  return hasValidSelection.value
    ? props.buildVideoDescription(currentSelectionLabels.value)
    : props.texts.emptyDescription;
});

const uploadDate = computed(() => new Date().toISOString().split('T')[0]);

const videoDuration = computed(() => {
  const totalSeconds = Math.floor(duration.value);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `PT${minutes}M${seconds}S`;
});

const calculateMenuPosition = (triggerElement: HTMLElement) => {
  const rect = triggerElement.getBoundingClientRect();
  return {
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  };
};

const updateMenuPosition = (
  menuElement: HTMLElement | null,
  triggerElement: HTMLElement | null,
) => {
  if (!menuElement || !triggerElement) return;
  const position = calculateMenuPosition(triggerElement);
  menuElement.style.top = position.top;
  menuElement.style.left = position.left;
  menuElement.style.width = position.width;
};

const handleFilterChange = () => {
  emit('filter-change', currentSelection.value);
};

const selectLanguage = (value: string) => {
  selectedLanguage.value = value;
};

const selectLanguageFromMobileDropdown = (value: string) => {
  selectedLanguage.value = value;
  languageMobileDropdownRef.value?.close();
};

const selectGender = (value: string) => {
  selectedGender.value = value;
  showGenderDropdown.value = false;
  handleFilterChange();
};

const selectGenderFromMobileDropdown = (value: string) => {
  selectedGender.value = value;
  genderMobileDropdownRef.value?.close();
  handleFilterChange();
};

const selectStyle = (value: string) => {
  selectedStyle.value = value;
  showStyleDropdown.value = false;
  handleFilterChange();
};

const selectStyleFromMobileDropdown = (value: string) => {
  selectedStyle.value = value;
  styleMobileDropdownRef.value?.close();
  handleFilterChange();
};

const closeOtherMobileDropdowns = (type: 'language' | 'gender' | 'style') => {
  if (type !== 'language') {
    languageMobileDropdownRef.value?.close();
  }
  if (type !== 'gender') {
    genderMobileDropdownRef.value?.close();
  }
  if (type !== 'style') {
    styleMobileDropdownRef.value?.close();
  }
};

const closeAllMobileDropdowns = () => {
  languageMobileDropdownRef.value?.close();
  genderMobileDropdownRef.value?.close();
  styleMobileDropdownRef.value?.close();
};

const toggleGenderDropdown = () => {
  showGenderDropdown.value = !showGenderDropdown.value;
  showStyleDropdown.value = false;
  closeAllMobileDropdowns();

  if (showGenderDropdown.value) {
    nextTick(() => {
      updateMenuPosition(genderMenuRef.value, genderDropdownRef.value);
    });
  }
};

const toggleStyleDropdown = () => {
  showStyleDropdown.value = !showStyleDropdown.value;
  showGenderDropdown.value = false;
  closeAllMobileDropdowns();

  if (showStyleDropdown.value) {
    nextTick(() => {
      updateMenuPosition(styleMenuRef.value, styleDropdownRef.value);
    });
  }
};

const closeDropdowns = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (
    !target.closest('.side-menu-player__dropdown') &&
    !target.closest('.side-menu-player__dropdown-menu')
  ) {
    showGenderDropdown.value = false;
    showStyleDropdown.value = false;
  }
};

const handleScroll = () => {
  if (showGenderDropdown.value) {
    updateMenuPosition(genderMenuRef.value, genderDropdownRef.value);
  }
  if (showStyleDropdown.value) {
    updateMenuPosition(styleMenuRef.value, styleDropdownRef.value);
  }
};

const togglePlay = () => {
  if (!videoRef.value) return;
  if (isPlaying.value) {
    videoRef.value.pause();
  } else {
    videoRef.value.play();
  }
};

const handleProgressClick = (event: MouseEvent) => {
  if (!videoRef.value || !duration.value) return;
  const target = event.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  const percent = (event.clientX - rect.left) / rect.width;
  videoRef.value.currentTime = percent * duration.value;
};

const handlePlay = () => {
  isPlaying.value = true;
  emit('video-play');
};

const handlePause = () => {
  isPlaying.value = false;
  emit('video-pause');
};

const handleEnded = () => {
  isPlaying.value = false;
  emit('video-ended');
};

const handleTimeUpdate = () => {
  if (videoRef.value) {
    currentTime.value = videoRef.value.currentTime;
  }
};

const handleLoadedMetadata = () => {
  if (videoRef.value) {
    duration.value = videoRef.value.duration;
  }
};

const handleCanPlay = () => {
  isVideoReady.value = true;
};

const handleError = (error: Event) => {
  emit('video-error', error);
};

const play = () => {
  videoRef.value?.play();
};

const pause = () => {
  videoRef.value?.pause();
};

const seek = (time: number) => {
  if (videoRef.value) {
    videoRef.value.currentTime = time;
  }
};

defineExpose({
  play,
  pause,
  seek,
  videoRef,
});

watch(selectedLanguage, newLanguage => {
  const selectedLang = props.languageOptions.find(lang => lang.value === newLanguage);
  if (selectedLang && selectedLang.genders.length > 0) {
    const isGenderAvailable = selectedLang.genders.some(g => g.value === selectedGender.value);
    if (!isGenderAvailable) {
      selectedGender.value = selectedLang.genders[0].value;
    }

    const selectedGenderOption = selectedLang.genders.find(g => g.value === selectedGender.value);
    if (selectedGenderOption && selectedGenderOption.styles.length > 0) {
      selectedStyle.value = selectedGenderOption.styles[0].value;
    }
  }
  handleFilterChange();
});

watch(selectedGender, newGender => {
  const selectedLang = props.languageOptions.find(lang => lang.value === selectedLanguage.value);
  const selectedGenderOption = selectedLang?.genders.find(g => g.value === newGender);

  if (selectedGenderOption && selectedGenderOption.styles.length > 0) {
    selectedStyle.value = selectedGenderOption.styles[0].value;
  }
  handleFilterChange();
});

watch(videoUrl, (newUrl, oldUrl) => {
  if (newUrl && videoRef.value && newUrl !== oldUrl) {
    videoRef.value.pause();
    isPlaying.value = false;
    currentTime.value = 0;
    isVideoReady.value = false;
    videoRef.value.currentTime = 0;
    videoRef.value.load();
  }
});

onMounted(() => {
  if (videoRef.value && hasValidSelection.value) {
    videoRef.value.load();
  }

  document.addEventListener('click', closeDropdowns);
  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleScroll);

  observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        if (videoRef.value && !videoRef.value.paused) {
          videoRef.value.pause();
          videoRef.value.currentTime = 0;
          isPlaying.value = false;
          currentTime.value = 0;
          emit('video-pause');
        } else if (videoRef.value && videoRef.value.currentTime > 0) {
          videoRef.value.currentTime = 0;
          currentTime.value = 0;
        }
      }
    },
    { threshold: 0 },
  );

  if (sideMenuPlayerRef.value) {
    observer.observe(sideMenuPlayerRef.value);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdowns);
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', handleScroll);

  if (observer) {
    observer.disconnect();
    observer = null;
  }
});
</script>

<style lang="scss" src="./mobile.scss" scoped />
<style lang="scss" src="./pc.scss" scoped />
